import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalculateIcon from '@mui/icons-material/Calculate';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import api from '../../services/api';
import {
  getStatusLabel,
  EVALUATION_STATUS_FILTER_OPTIONS,
  type EvaluationStatus,
  type GoalApprovalStatus
} from '../../utils/statusLabels';
import { extractErrorMessage } from '../../utils/errorHandler';
import { todayStringJST } from '../../utils/dateUtils';

interface Evaluation {
  employee_id: string;
  employee_name: string;
  department_id: string;
  department_name: string;
  department_type: string;
  job_grade: string;
  job_title: string;
  rank: string;
  rank_cost: number;
  evaluation_id: string | null;
  evaluation_period: string | null;
  status: string | null;
  goal_approval_status: string | null;
  performance_score: number | null;
  salary_increase_rate: number | null;
  updated_at: string | null;
  /** 評価ルートが設定されているか（評価行なしの表示を出し分ける） */
  has_route: boolean;
  /** 上長評価無効ルート（部門長が上長役を兼務）か */
  manager_eval_disabled: boolean;
}

interface EvaluationPeriod {
  period_id: string;
  period_name: string;
  period_type: string;
  goal_setting_start: string;
  goal_setting_end: string;
  self_eval_start: string;
  self_eval_end: string;
  manager_eval_start: string;
  manager_eval_end: string;
  dept_head_eval_start: string;
  dept_head_eval_end: string;
  target_average_increase_rate: number;
  company_adjustment_rate: number;
  is_adjustment_rate_locked: boolean;
  is_active: boolean;
  status: string;
}

interface DepartmentBreakdown {
  department_type: string;
  employee_count: number;
  avg_performance_score: number;
  avg_base_rate: number;
  avg_years_of_service: number;
}

interface RCalculationResult {
  period_id: string;
  period_name: string;
  target_average_increase_rate: number;
  total_employees: number;
  average_base_rate: number;
  required_adjustment_rate: number;
  department_breakdown: DepartmentBreakdown[];
}

const HRAllEvaluationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);

  const [periodId, setPeriodId] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [departments, setDepartments] = useState<Array<{department_id: string; department_name: string}>>([]);
  const [evaluationPeriods, setEvaluationPeriods] = useState<EvaluationPeriod[]>([]);

  // R計算ダイアログ関連の状態
  const [rCalcDialogOpen, setRCalcDialogOpen] = useState(false);
  const [rCalcLoading, setRCalcLoading] = useState(false);
  const [rCalcResult, setRCalcResult] = useState<RCalculationResult | null>(null);
  const [rCalcError, setRCalcError] = useState('');

  // Rロック解除ダイアログ関連の状態
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  useEffect(() => {
    const initialize = async () => {
      await fetchEvaluationPeriods();
      await fetchDepartments();
      setLoading(false);
    };
    initialize();
  }, []);

  useEffect(() => {
    if (periodId) {
      fetchEvaluations();
    } else {
      setLoading(false);
      setEvaluations([]);
    }
  }, [periodId, departmentFilter, statusFilter, searchQuery]);

  const fetchEvaluationPeriods = async () => {
    try {
      const response = await api.get('/evaluation-periods');
      const periodsData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setEvaluationPeriods(periodsData);

      // デフォルトでアクティブな評価期間を選択
      if (periodsData.length > 0) {
        const activePeriod = periodsData.find((p: EvaluationPeriod) => p.is_active);
        setPeriodId(activePeriod ? activePeriod.period_id : periodsData[0].period_id);
      }
    } catch (err) {
      console.error('評価期間取得エラー:', err);
      setEvaluationPeriods([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      // レスポンスが配列かオブジェクトかチェック
      const deptData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setDepartments(deptData);
    } catch (err) {
      console.error('部門取得エラー:', err);
      setDepartments([]); // エラー時は空配列をセット
    }
  };

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      setError('');

      const params: any = { period_id: periodId };
      if (departmentFilter && departmentFilter !== 'all') params.department_id = departmentFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get('/hr/evaluations', {
        params,
      });

      if (response.data.success) {
        setEvaluations(response.data.data);
      }
    } catch (err) {
      console.error('評価一覧取得エラー:', err);
      setError(extractErrorMessage(err, '評価一覧の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (employeeId: string) => {
    navigate(`/hr/evaluations/${employeeId}/${periodId}`);
  };

  // R計算ダイアログを開く
  const handleOpenRCalcDialog = async () => {
    if (!periodId) {
      setError('評価期間を選択してください');
      return;
    }

    setRCalcDialogOpen(true);
    setRCalcLoading(true);
    setRCalcError('');
    setRCalcResult(null);

    try {
      const response = await api.post(
        `/evaluation-periods/${periodId}/calculate-adjustment-rate`,
        {}
      );

      if (response.data.success) {
        setRCalcResult(response.data.data);
      }
    } catch (err) {
      console.error('R計算エラー:', err);
      setRCalcError(extractErrorMessage(err, 'R計算に失敗しました'));
    } finally {
      setRCalcLoading(false);
    }
  };

  // R確定・昇給率一括計算
  const handleConfirmR = async () => {
    if (!rCalcResult) return;

    setRCalcLoading(true);
    setRCalcError('');

    try {
      const response = await api.put(
        `/evaluation-periods/${periodId}/lock-adjustment-rate`,
        {
          company_adjustment_rate: rCalcResult.required_adjustment_rate,
        }
      );

      if (response.data.success) {
        setRCalcDialogOpen(false);
        setRCalcResult(null);
        // 評価一覧を再取得
        fetchEvaluations();
        fetchEvaluationPeriods();
      }
    } catch (err) {
      console.error('R確定エラー:', err);
      setRCalcError(extractErrorMessage(err, 'R確定に失敗しました'));
    } finally {
      setRCalcLoading(false);
    }
  };

  // R計算ダイアログを閉じる
  const handleCloseRCalcDialog = () => {
    setRCalcDialogOpen(false);
    setRCalcResult(null);
    setRCalcError('');
  };

  // Rロック解除ダイアログを開く
  const handleOpenUnlockDialog = () => {
    setUnlockDialogOpen(true);
    setUnlockError('');
  };

  // Rロック解除を実行
  const handleUnlockR = async () => {
    if (!periodId) return;

    setUnlockLoading(true);
    setUnlockError('');

    try {
      const response = await api.put(
        `/evaluation-periods/${periodId}/unlock-adjustment-rate`,
        {}
      );

      if (response.data.success) {
        setUnlockDialogOpen(false);
        // 評価一覧と評価期間を再取得
        fetchEvaluations();
        fetchEvaluationPeriods();
      }
    } catch (err) {
      console.error('Rロック解除エラー:', err);
      setUnlockError(extractErrorMessage(err, 'Rロック解除に失敗しました'));
    } finally {
      setUnlockLoading(false);
    }
  };

  // Rロック解除ダイアログを閉じる
  const handleCloseUnlockDialog = () => {
    setUnlockDialogOpen(false);
    setUnlockError('');
  };

  // 部門種別の日本語表示
  const getDepartmentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      tech: '技術部門',
      sales: '営業部門',
      admin: '管理部門',
    };
    return labels[type] || type;
  };

  // 現在選択中の評価期間を取得
  const currentPeriod = evaluationPeriods.find(p => p.period_id === periodId);

  // ステータス表示の出し分けに使う期間情報
  // （設計書類/02_評価管理機能/画面設計_評価ステータス表記統一.md §3.4）
  const selfEvalStarted = currentPeriod?.self_eval_start
    ? todayStringJST() >= currentPeriod.self_eval_start.slice(0, 10)
    : undefined;
  const periodClosed = currentPeriod ? currentPeriod.status === 'closed' : undefined;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            HR評価管理
          </Typography>
          {currentPeriod?.is_adjustment_rate_locked && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip
                icon={<LockIcon />}
                label={`R確定済: ${Number(currentPeriod.company_adjustment_rate).toFixed(2)}%`}
                color="success"
                variant="outlined"
              />
              <Button
                variant="outlined"
                color="warning"
                startIcon={<LockOpenIcon />}
                onClick={handleOpenUnlockDialog}
              >
                R値ロック解除
              </Button>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {evaluationPeriods.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            評価期間が登録されていません。評価期間管理画面から評価期間を作成してください。
          </Alert>
        )}

        {/* フィルタ */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>評価期間</InputLabel>
                <Select
                  value={periodId}
                  onChange={(e) => setPeriodId(e.target.value)}
                  label="評価期間"
                >
                  {evaluationPeriods.map((period) => (
                    <MenuItem key={period.period_id} value={period.period_id}>
                      {period.period_name}
                      {period.is_active && ' (有効)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>部門</InputLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  label="部門"
                >
                  <MenuItem value="all">すべて</MenuItem>
                  {Array.isArray(departments) && departments.map((dept) => (
                    <MenuItem key={dept.department_id} value={dept.department_id}>
                      {dept.department_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="ステータス"
                >
                  <MenuItem value="all">すべて</MenuItem>
                  {EVALUATION_STATUS_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="社員名・ID検索"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="社員名またはIDで検索"
              />
            </Grid>
          </Grid>
        </Box>

        {/* 評価一覧テーブル */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>社員ID</TableCell>
                <TableCell>社員名</TableCell>
                <TableCell>部門</TableCell>
                <TableCell>職級</TableCell>
                <TableCell>役職</TableCell>
                <TableCell>ランク</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell align="right">評価点(P)</TableCell>
                <TableCell align="right">昇給率(%)</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {evaluations.map((evaluation) => {
                const statusInfo = getStatusLabel(
                  evaluation.status as EvaluationStatus,
                  evaluation.goal_approval_status as GoalApprovalStatus,
                  {
                    hasRoute: evaluation.has_route,
                    managerEvalDisabled: evaluation.manager_eval_disabled,
                    selfEvalStarted,
                    periodClosed
                  }
                );
                return (
                  <TableRow key={evaluation.employee_id} hover>
                    <TableCell>{evaluation.employee_id}</TableCell>
                    <TableCell>{evaluation.employee_name}</TableCell>
                    <TableCell>{evaluation.department_name}</TableCell>
                    <TableCell>{evaluation.job_grade}</TableCell>
                    <TableCell>{evaluation.job_title}</TableCell>
                    <TableCell>{evaluation.rank}</TableCell>
                    <TableCell>
                      <Chip label={statusInfo.label} color={statusInfo.chipColor} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      {evaluation.performance_score !== null && evaluation.performance_score !== undefined
                        ? Number(evaluation.performance_score).toFixed(2)
                        : '−'}
                    </TableCell>
                    <TableCell align="right">
                      {evaluation.salary_increase_rate !== null && evaluation.salary_increase_rate !== undefined
                        ? Number(evaluation.salary_increase_rate).toFixed(2) + '%'
                        : '−'}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetail(evaluation.employee_id)}
                      >
                        詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {evaluations.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              該当する評価データがありません
            </Typography>
          </Box>
        )}
      </Paper>

      {/* R計算ダイアログ */}
      <Dialog
        open={rCalcDialogOpen}
        onClose={handleCloseRCalcDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          会社調整率（R）計算・確定
        </DialogTitle>
        <DialogContent dividers>
          {rCalcLoading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>計算中...</Typography>
            </Box>
          )}

          {rCalcError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rCalcError}
            </Alert>
          )}

          {rCalcResult && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                この計算結果に基づいてRを確定すると、全従業員の昇給率が一括計算されます。
                確定後はRの変更はできません。
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {rCalcResult.period_name}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'white' }}>
                    <Typography variant="body2">目標平均昇給率</Typography>
                    <Typography variant="h4">
                      {Number(rCalcResult.target_average_increase_rate).toFixed(2)}%
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
                    <Typography variant="body2">必要な会社調整率（R）</Typography>
                    <Typography variant="h4">
                      {Number(rCalcResult.required_adjustment_rate).toFixed(2)}%
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.200' }}>
                    <Typography variant="body2">対象従業員数</Typography>
                    <Typography variant="h5">
                      {rCalcResult.total_employees}人
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.200' }}>
                    <Typography variant="body2">平均基準昇給率（R=0時）</Typography>
                    <Typography variant="h5">
                      {Number(rCalcResult.average_base_rate).toFixed(2)}%
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    部門別内訳
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>部門</TableCell>
                          <TableCell align="right">人数</TableCell>
                          <TableCell align="right">平均P点</TableCell>
                          <TableCell align="right">平均勤続年数</TableCell>
                          <TableCell align="right">平均基準昇給率</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rCalcResult.department_breakdown.map((dept) => (
                          <TableRow key={dept.department_type}>
                            <TableCell>{getDepartmentTypeLabel(dept.department_type)}</TableCell>
                            <TableCell align="right">{dept.employee_count}人</TableCell>
                            <TableCell align="right">
                              {Number(dept.avg_performance_score).toFixed(2)}点
                            </TableCell>
                            <TableCell align="right">
                              {Number(dept.avg_years_of_service).toFixed(1)}年
                            </TableCell>
                            <TableCell align="right">
                              {Number(dept.avg_base_rate).toFixed(2)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      確定前の確認事項
                    </Typography>
                    <Typography variant="body2" component="div">
                      • 全従業員の評価（P点）が完了していることを確認してください
                      <br />
                      • 確定後はR値の変更はできません
                      <br />
                      • 確定により全従業員の昇給率が自動計算されます
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRCalcDialog} disabled={rCalcLoading}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmR}
            variant="contained"
            color="primary"
            disabled={rCalcLoading || !rCalcResult}
            startIcon={<LockIcon />}
          >
            R確定・昇給率一括計算
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rロック解除確認ダイアログ */}
      <Dialog
        open={unlockDialogOpen}
        onClose={handleCloseUnlockDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          R値ロック解除の確認
        </DialogTitle>
        <DialogContent dividers>
          {unlockLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>処理中...</Typography>
            </Box>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>警告: この操作は慎重に実行してください</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  R値のロックを解除すると、以下の影響があります：
                  <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                    <li>r_appliedまたはfinalizedステータスの評価がdept_evaluatedに戻ります</li>
                    <li>全従業員の昇給率がNULLにリセットされます</li>
                    <li>finalizedのHR承認情報が削除されます</li>
                    <li>評価ルートの追加・削除が可能になります</li>
                    <li>R値の再計算・適用が必要になります</li>
                  </ul>
                </Typography>
              </Alert>

              {unlockError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {unlockError}
                </Alert>
              )}

              <Typography variant="body1" sx={{ mt: 2 }}>
                評価期間: <strong>{currentPeriod?.period_name}</strong>
              </Typography>
              <Typography variant="body1">
                現在のR値: <strong>{Number(currentPeriod?.company_adjustment_rate).toFixed(2)}%</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnlockDialog} disabled={unlockLoading}>
            キャンセル
          </Button>
          <Button
            onClick={handleUnlockR}
            variant="contained"
            color="warning"
            disabled={unlockLoading}
            startIcon={<LockOpenIcon />}
          >
            ロック解除を実行
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRAllEvaluationsPage;
